import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Skeleton,
  Avatar,
  Chip,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const glassMixin = {
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  background: "rgba(255,255,255,0.65)",
  border: "1px solid rgba(255,255,255,0.85)",
  boxShadow:
    "0 8px 32px rgba(245,158,11,0.07), 0 1px 0 rgba(255,255,255,0.9) inset",
};

const RANK_STYLES = {
  1: {
    bg: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    light: "rgba(245,158,11,0.1)",
    text: "#d97706",
    border: "rgba(245,158,11,0.25)",
    icon: "🥇",
  },
  2: {
    bg: "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)",
    light: "rgba(100,116,139,0.08)",
    text: "#64748b",
    border: "rgba(100,116,139,0.2)",
    icon: "🥈",
  },
  3: {
    bg: "linear-gradient(135deg, #cd7f32 0%, #a0522d 100%)",
    light: "rgba(160,82,45,0.08)",
    text: "#92400e",
    border: "rgba(160,82,45,0.2)",
    icon: "🥉",
  },
};

const AVATAR_COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#06b6d4",
  "#ec4899",
];

const getInitials = (name) =>
  (name || "?")
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

const getScoreStyle = (score) => {
  if (score >= 85)
    return {
      color: "#10b981",
      bg: "rgba(16,185,129,0.1)",
      gradient: "linear-gradient(90deg, #10b981, #34d399)",
    };
  if (score >= 70)
    return {
      color: "#6366f1",
      bg: "rgba(99,102,241,0.1)",
      gradient: "linear-gradient(90deg, #6366f1, #818cf8)",
    };
  if (score >= 55)
    return {
      color: "#f59e0b",
      bg: "rgba(245,158,11,0.1)",
      gradient: "linear-gradient(90deg, #f59e0b, #fbbf24)",
    };
  return {
    color: "#ef4444",
    bg: "rgba(239,68,68,0.1)",
    gradient: "linear-gradient(90deg, #ef4444, #f87171)",
  };
};

const LeaderboardRow = ({ student, index, onNavigate }) => {
  const rank = index + 1;
  const rankStyle = RANK_STYLES[rank];
  const name = student.inststudent__name || student.name || "Unknown";
  const score = parseFloat(student.avg || student.score || 0).toFixed(1);
  const scoreStyle = getScoreStyle(parseFloat(score));

  return (
    <MotionBox
      initial={{ opacity: 0, x: -24 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        duration: 0.45,
        delay: index * 0.07,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{ x: 6, transition: { duration: 0.2 } }}
      onClick={() => onNavigate && onNavigate(student)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: "13px 16px",
        borderRadius: "16px",
        cursor: onNavigate ? "pointer" : "default",
        border:
          rank <= 3 ? `1px solid ${rankStyle.border}` : "1px solid transparent",
        background: rank <= 3 ? rankStyle.light : "transparent",
        transition: "all 0.2s ease",
        "&:hover": {
          background: rank <= 3 ? rankStyle.light : "rgba(99,102,241,0.04)",
          border:
            rank <= 3
              ? `1px solid ${rankStyle.border}`
              : "1px solid rgba(99,102,241,0.12)",
        },
      }}
    >
      {/* rank */}
      <Box sx={{ width: 34, textAlign: "center", flexShrink: 0 }}>
        {rank <= 3 ? (
          <Typography sx={{ fontSize: "1.5rem", lineHeight: 1 }}>
            {rankStyle.icon}
          </Typography>
        ) : (
          <Typography
            sx={{
              fontSize: "0.82rem",
              fontWeight: 800,
              color: "#9ca3af",
              fontFamily: "'Syne', sans-serif",
            }}
          >
            #{rank}
          </Typography>
        )}
      </Box>

      {/* avatar */}
      <Avatar
        sx={{
          width: 38,
          height: 38,
          fontSize: "0.78rem",
          fontWeight: 700,
          background:
            rank <= 3
              ? rankStyle.bg
              : AVATAR_COLORS[(index - 3) % AVATAR_COLORS.length],
          flexShrink: 0,
          boxShadow: rank === 1 ? "0 3px 12px rgba(245,158,11,0.45)" : "none",
          fontFamily: "'Syne', sans-serif",
        }}
      >
        {getInitials(name)}
      </Avatar>

      {/* name + bar */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 700,
            color: "#111827",
            fontSize: "0.875rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontFamily: "'Syne', sans-serif",
          }}
        >
          {name}
        </Typography>
        <Box
          sx={{
            height: 4,
            borderRadius: "100px",
            mt: 0.6,
            background: "rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${parseFloat(score)}%` }}
            transition={{
              duration: 1.2,
              delay: index * 0.07 + 0.5,
              ease: [0.22, 1, 0.36, 1],
            }}
            style={{
              height: "100%",
              background: rank <= 3 ? rankStyle.bg : scoreStyle.gradient,
              borderRadius: "100px",
            }}
          />
        </Box>
      </Box>

      {/* score chip */}
      <Chip
        label={`${score}%`}
        size="small"
        sx={{
          background: scoreStyle.bg,
          color: scoreStyle.color,
          fontWeight: 800,
          fontSize: "0.78rem",
          height: 26,
          border: `1px solid ${scoreStyle.color}30`,
          borderRadius: "8px",
          flexShrink: 0,
          fontFamily: "'Syne', sans-serif",
        }}
      />

      {onNavigate && (
        <ArrowForwardRoundedIcon
          sx={{
            color: "#d1d5db",
            fontSize: 15,
            flexShrink: 0,
            transition: "color 0.2s",
            ".MuiBox-root:hover &": { color: "#6366f1" },
          }}
        />
      )}
    </MotionBox>
  );
};

const StudentLeaderboard = ({ students, filters, onNavigate }) => {
  if (!students) {
    return (
      <Card sx={{ mb: 3, borderRadius: "24px", ...glassMixin }}>
        <CardContent sx={{ p: "24px !important" }}>
          <Skeleton width={180} height={24} sx={{ mb: 1 }} />
          <Skeleton width={240} height={16} sx={{ mb: 3 }} />
          {[...Array(5)].map((_, i) => (
            <Stack
              key={i}
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mb: 1.5 }}
            >
              <Skeleton variant="circular" width={36} height={36} />
              <Skeleton variant="circular" width={38} height={38} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="55%" height={14} sx={{ mb: 0.5 }} />
                <Skeleton width="100%" height={4} />
              </Box>
              <Skeleton
                variant="rounded"
                width={52}
                height={26}
                sx={{ borderRadius: "8px" }}
              />
            </Stack>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (!students.length) {
    return (
      <Card sx={{ mb: 3, borderRadius: "24px", ...glassMixin }}>
        <CardContent
          sx={{
            p: "24px !important",
            textAlign: "center",
            py: "52px !important",
          }}
        >
          <EmojiEventsRoundedIcon
            sx={{ fontSize: 48, color: "#d1d5db", mb: 1.5 }}
          />
          <Typography
            variant="h6"
            sx={{
              color: "#9ca3af",
              fontFamily: "'Syne', sans-serif",
              fontWeight: 700,
            }}
          >
            No Students Yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
            Leaderboard will appear once data is available.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <MotionCard
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      sx={{
        mb: 3,
        borderRadius: "24px",
        overflow: "hidden",
        position: "relative",
        ...glassMixin,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background:
            "linear-gradient(90deg, #f59e0b 0%, #fbbf24 60%, #f59e0b 100%)",
        }}
      />

      <CardContent sx={{ p: "24px !important" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(251,191,36,0.1) 100%)",
                  border: "1px solid rgba(245,158,11,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <WorkspacePremiumRoundedIcon
                  sx={{ color: "#f59e0b", fontSize: 20 }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Syne', sans-serif",
                  fontWeight: 700,
                  color: "#111827",
                  fontSize: "1rem",
                }}
              >
                Top Students
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{ color: "#9ca3af", ml: "54px", fontSize: "0.78rem" }}
            >
              {students.length} students ranked by performance
            </Typography>
          </Box>

          <Chip
            label={`${students.length} ranked`}
            size="small"
            sx={{
              background: "rgba(99,102,241,0.08)",
              color: "#6366f1",
              fontWeight: 700,
              fontSize: "0.72rem",
              border: "1px solid rgba(99,102,241,0.2)",
              borderRadius: "10px",
            }}
          />
        </Stack>

        <Stack spacing={0.5}>
          <AnimatePresence>
            {students.map((student, index) => (
              <LeaderboardRow
                key={student.id || index}
                student={student}
                index={index}
                onNavigate={onNavigate}
              />
            ))}
          </AnimatePresence>
        </Stack>
      </CardContent>
    </MotionCard>
  );
};

export default StudentLeaderboard;
